import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { LEAD_SOURCE, LEAD_INTEREST, PRIORITY } from "../../lib/enums";
import { LEAD_SOURCE_LABELS, LEAD_INTEREST_LABELS, PRIORITY_LABELS } from "../../lib/labels";
import { api, extractApiError } from "../../lib/api";
import { toast } from "sonner";
import { EMPTY_LEAD_FORM, applyCompanySelection, applyContactSelection, buildInitialLeadForm } from "../../lib/lead-relations";
import { getLeadStatusOptionsForCurrentStatus } from "../../lib/lead-status-options";

const NONE = "__none__";

export default function LeadFormDialog({ open, onOpenChange, onSaved, initialCompany, initialContact }) {
  const [form, setForm] = useState(EMPTY_LEAD_FORM);
  const [saving, setSaving] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loadingRefs, setLoadingRefs] = useState(false);

  const handleChange = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  useEffect(() => {
    if (!open) return;

    setLoadingRefs(true);
    Promise.all([api.get("/api/companies"), api.get("/api/contacts")])
      .then(([co, ct]) => {
        setCompanies(co.data || []);
        setContacts(ct.data || []);
      })
      .catch((e) => {
        toast.error(extractApiError(e, "Referenzen konnten nicht geladen werden."));
      })
      .finally(() => setLoadingRefs(false));

    setForm(buildInitialLeadForm({ initialCompany, initialContact }));
  }, [open, initialCompany, initialContact]);

  const handleCompanyChange = (v) => {
    if (v === NONE) {
      setForm((current) => applyCompanySelection({ form: current, company: null }));
      return;
    }
    const selected = companies.find((c) => c.id === v);
    setForm((current) => applyCompanySelection({ form: current, company: selected }));
  };

  const handleContactChange = (v) => {
    if (v === NONE) {
      setForm((current) => ({ ...current, contact_id: null }));
      return;
    }
    const selected = contacts.find((c) => c.id === v);
    const selectedCompany = selected?.company_id ? companies.find((c) => c.id === selected.company_id) : null;
    setForm((current) =>
      applyContactSelection({ form: current, contact: selected, companyForContact: selectedCompany })
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      toast.error("Bitte einen Titel angeben.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/api/leads", form);
      toast.success("Lead erstellt.");
      setForm(EMPTY_LEAD_FORM);
      onSaved?.();
    } catch (err) {
      toast.error(extractApiError(err, "Lead konnte nicht erstellt werden."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Lead erstellen</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Titel *" full>
            <Input
              data-testid="lead-form-title"
              value={form.title}
              onChange={(e) => handleChange("title", e.target.value)}
              autoFocus
            />
          </Field>
          <Field label="Kontakt (CRM-Verknüpfung)">
            <Select
              value={form.contact_id || NONE}
              onValueChange={handleContactChange}
              disabled={saving || loadingRefs}
            >
              <SelectTrigger><SelectValue placeholder="Keinen Kontakt verknüpfen" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>Keinen Kontakt verknüpfen</SelectItem>
                {contacts.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.first_name} {c.last_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Firma (CRM-Verknüpfung)">
            <Select
              value={form.company_id || NONE}
              onValueChange={handleCompanyChange}
              disabled={saving || loadingRefs}
            >
              <SelectTrigger><SelectValue placeholder="Keine Firma verknüpfen" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>Keine Firma verknüpfen</SelectItem>
                {companies.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Kontaktname (Anfragedaten)">
            <Input value={form.contact_name} onChange={(e) => handleChange("contact_name", e.target.value)} />
          </Field>
          <Field label="E-Mail (Anfragedaten)">
            <Input value={form.contact_email} onChange={(e) => handleChange("contact_email", e.target.value)} />
          </Field>
          <Field label="Telefon (Anfragedaten)">
            <Input value={form.contact_phone} onChange={(e) => handleChange("contact_phone", e.target.value)} />
          </Field>
          <Field label="Firma (Anfragedaten)">
            <Input value={form.company_name} onChange={(e) => handleChange("company_name", e.target.value)} />
          </Field>
          <Field label="Quelle">
            <Select value={form.source} onValueChange={(v) => handleChange("source", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.values(LEAD_SOURCE).map((s) => (
                  <SelectItem key={s} value={s}>{LEAD_SOURCE_LABELS[s]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Interesse">
            <Select value={form.interest} onValueChange={(v) => handleChange("interest", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.values(LEAD_INTEREST).map((s) => (
                  <SelectItem key={s} value={s}>{LEAD_INTEREST_LABELS[s]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Status">
            <Select value={form.status} onValueChange={(v) => handleChange("status", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {getLeadStatusOptionsForCurrentStatus(form.status).map((o) => (
                  <SelectItem key={o.value} value={o.value} disabled={o.disabled}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Priorität">
            <Select value={form.priority} onValueChange={(v) => handleChange("priority", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.values(PRIORITY).map((s) => (
                  <SelectItem key={s} value={s}>{PRIORITY_LABELS[s].de}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Beschreibung" full>
            <Textarea
              rows={3}
              value={form.description}
              onChange={(e) => handleChange("description", e.target.value)}
            />
          </Field>
          <DialogFooter className="md:col-span-2 mt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange?.(false)}>
              Abbrechen
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="text-white"
              style={{ background: "var(--brand)" }}
              data-testid="lead-form-submit"
            >
              {saving ? "Speichern …" : "Erstellen"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children, full = false }) {
  return (
    <div className={`space-y-1.5 ${full ? "md:col-span-2" : ""}`}>
      <Label className="text-xs text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}
